import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { EMPTY } from "rxjs";
import {
  catchError,
  delay,
  switchMap,
  finalize,
  tap,
  take,
} from "rxjs/operators";
import { AuthService } from "src/app/core/service/auth/auth.service";
import { showFeedbackSaved } from "src/app/core/util/notification";
import { Globals } from "src/app/globals";
import { TranslocoService } from "@jsverse/transloco";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ProjectsService } from "src/app/akita/project/state/projects.service";
import Bugsnag from "@bugsnag/js";

@Component({
  selector: "app-two-factor-authentication",
  templateUrl: "./two-factor-authentication.component.html",
  styleUrls: ["./two-factor-authentication.component.sass"],
  // (optional) turn this on when you're ready:
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwoFactorAuthenticationComponent implements OnInit {
  public password: string;
  public showError = false;
  public code: string;
  public verify = false;
  public invalidHint = false;
  public QRCode: any;
  public passwordToggle = false;
  public recoveryCodes: any;
  public email!: string;
  public mfaSetupNeedsRestart = false;
  public mfaSetupRestarting = false;
  public mfaConfirmationSaving = false;

  translatedText: SafeHtml = "";
  public qrTrustedHtml?: SafeHtml;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private sanitizer: DomSanitizer,
    public dialogRef: MatDialogRef<TwoFactorAuthenticationComponent>,
    private _authService: AuthService,
    public globals: Globals,
    private translocoService: TranslocoService,
    private projectsService: ProjectsService,
    private cd: ChangeDetectorRef
  ) {
    this.applyColorToTranslatedText();
  }

  ngOnInit() {
    this.email = this.globals?.user?.email ?? this.data.email;
  }

  private setQrFromResponse(svgRaw: string) {
    const svgWithHeight = svgRaw.replace(
      /<svg(\s|>)/i,
      (_m) => `<svg height="115px"${_m === ">" ? "" : " "}`
    );
    this.qrTrustedHtml = this.sanitizer.bypassSecurityTrustHtml(svgWithHeight);
    this.cd.markForCheck();
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public onPasswordChange(): void {
    this.verify = false;
    this.invalidHint = true;
  }

  public checkValidPassword(status: boolean): void {
    this._authService
      .getAuthToken(this.email, this.password)
      .pipe(
        tap(() => {
          if (status) {
            this.disableTwoFactorAuthentication();
          } else {
            this.verify = true;
            this.invalidHint = true;
            this.twoFactorAuthentication();
          }
        }),
        catchError(async (err) => {
          this.errorHandler();
          Bugsnag.notify(err);
        })
      )
      .subscribe();
  }

  public twoFactorAuthentication(): void {
    this.mfaSetupNeedsRestart = false;
    this.mfaSetupRestarting = true;
    this.qrTrustedHtml = undefined;
    this.recoveryCodes = undefined;

    this._authService
      .enableMultiFactorAuthentification()
      .pipe(
        // keep delay if API needs time to persist (otherwise remove)
        delay(2000),
        switchMap(() => this._authService.getMultiFactorAuthentificationQR()),
        tap((res) => {
          this.setQrFromResponse(res.svg);

          if (this.data.viaSettings) {
            this.projectsService
              .setActivityLogs("user_two_factor_auth_enabled")
              .subscribe();
          }
          this.getRecoveryCodes();
        }),
        finalize(() => {
          this.mfaSetupRestarting = false;
          this.cd.markForCheck();
        }),
        catchError(async (err) => {
          if (err?.status === 409) {
            this.mfaSetupNeedsRestart = true;
            this.qrTrustedHtml = undefined;
            this.recoveryCodes = undefined;
            this.cd.markForCheck();
            return;
          }

          this.errorHandler();
          Bugsnag.notify(err);
        })
      )
      .subscribe();
  }

  public restartMfaSetup(): void {
    this.code = "";
    this.showError = false;
    this.twoFactorAuthentication();
  }

  public errorHandler(): void {
    this.verify = false;
    this.invalidHint = false;
  }

  public onSaveCode(): void {
    if (this.mfaConfirmationSaving) {
      return;
    }

    this.showError = false;
    this.mfaConfirmationSaving = true;

    this._authService
      .sendMultiFactorAuthentificationCode({ code: this.code })
      .pipe(
        tap(() => {
          this.dialogRef.close({ status: true });
          if (this.globals) {
            this.globals.user.is_mfa_enabled = true;
          }
          showFeedbackSaved();
        }),
        catchError((err) => {
          this.showError = true;
          Bugsnag.notify(err);

          return EMPTY;
        }),
        finalize(() => {
          this.mfaConfirmationSaving = false;
          this.cd.markForCheck();
        })
      )
      .subscribe();
  }

  public disableTwoFactorAuthentication(): void {
    this._authService
      .disableMultiFactorAuthentification()
      .pipe(
        finalize(() => {
          this.dialogRef.close({ status: false });
          if (this.globals) {
            this.globals.user.is_mfa_enabled = false;
          }
          this.projectsService
            .setActivityLogs("user_two_factor_auth_disabled")
            .subscribe();
          showFeedbackSaved();
        }),
        catchError(async (err) => Bugsnag.notify(err))
      )
      .subscribe();
  }

  public getRecoveryCodes() {
    this._authService
      .getRecoveryCode()
      .pipe(
        tap((res) => {
          this.recoveryCodes = res;
          this.cd.markForCheck();
        }),
        catchError(async (err) => {
          this.errorHandler();
          Bugsnag.notify(err);
        })
      )
      .subscribe();
  }

  applyColorToTranslatedText(): void {
    this.translocoService
      .selectTranslate("settings.personal.favorite_two-factor_authentication")
      .pipe(take(1)) // prevent leak
      .subscribe((translatedString: string) => {
        const highlightedText = this.highlightKeywords(translatedString);
        this.translatedText =
          this.sanitizer.bypassSecurityTrustHtml(highlightedText);
        this.cd.markForCheck();
      });
  }

  highlightKeywords(text: string): string {
    const keywords = ["Microsoft Authenticator", "Google Authenticator"];
    for (const keyword of keywords) {
      const regex = new RegExp(`(${keyword})`, "gi");
      text = text.replace(regex, `<span style="color:blue;">$1</span>`);
    }
    return text;
  }
}
